import { useState } from 'react'
import { toast } from 'sonner'
import { AlertCircle, Check, Loader2, Sparkles, X } from 'lucide-react'

import {
  useApplyAiAction,
  useDocumentAiActions,
  useRejectAiAction,
} from '@/api/queries'
import type {
  AiActionRead,
  AiActionType,
  AiFieldEditPayloadEntry,
} from '@/api/docfiller-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { formatActionError } from './ai-suggestion-utils'

/**
 * AI suggestions panel — renders the *pending* AiAction queue for a document
 * and exposes the explicit accept/reject boundary the backend enforces.
 *
 * Each pending action shows the proposed before/after values and gives the
 * user two affordances:
 *   - **Accept**: ``POST /dashboard/ai-actions/{uuid}/apply`` — server runs
 *     the change through the same documents-service helpers used by manual
 *     PATCH calls, so audit + checkbox canonicalisation + ownership checks
 *     all stay consistent.
 *   - **Reject**: ``POST .../reject`` — no field mutation, but the audit row
 *     persists so the proposal still appears in the history view.
 *
 * UX surface:
 *   - Loading skeletons while the list is in flight.
 *   - Empty state when nothing is pending.
 *   - Error state with retry when the GET fails.
 *   - Per-action inline spinners on the action that's being applied/rejected
 *     plus a busy state on its sibling button (no double-submits).
 *   - 429 / 409 / 422 from the backend get explicit toast copy so the user
 *     understands rate-limits, stale proposals, and validation rejections
 *     instead of seeing a generic "Apply failed".
 */
export function AiSuggestionsPanel({ documentId }: { documentId: string }) {
  const {
    data: actions,
    isLoading,
    isError,
    error,
    refetch,
  } = useDocumentAiActions(documentId, 'pending')
  const apply = useApplyAiAction(documentId)
  const reject = useRejectAiAction(documentId)
  const [busyUuid, setBusyUuid] = useState<string | null>(null)
  const [busyKind, setBusyKind] = useState<'accept' | 'reject' | null>(null)

  const onAccept = async (action: AiActionRead) => {
    if (busyUuid) return
    setBusyUuid(action.uuid)
    setBusyKind('accept')
    try {
      await apply.mutateAsync(action.uuid)
      toast.success('Suggestion accepted')
    } catch (err) {
      toast.error(formatActionError(err, 'Accept'))
    } finally {
      setBusyUuid(null)
      setBusyKind(null)
    }
  }

  const onReject = async (action: AiActionRead) => {
    if (busyUuid) return
    setBusyUuid(action.uuid)
    setBusyKind('reject')
    try {
      await reject.mutateAsync(action.uuid)
      toast.success('Suggestion dismissed')
    } catch (err) {
      toast.error(formatActionError(err, 'Reject'))
    } finally {
      setBusyUuid(null)
      setBusyKind(null)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="ai-suggestions-loading">
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    )
  }

  if (isError) {
    return (
      <Card
        tone="muted"
        className="space-y-3 border border-destructive-soft bg-destructive-soft/30 p-4"
        data-testid="ai-suggestions-error"
      >
        <div className="flex items-start gap-2 text-sm text-destructive-soft-foreground">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">Couldn't load AI suggestions</p>
            <p className="text-xs">
              {error instanceof Error
                ? error.message
                : 'Unknown error fetching suggestions.'}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void refetch()
          }}
        >
          Retry
        </Button>
      </Card>
    )
  }

  if (!actions || actions.length === 0) {
    return (
      <EmptyState
        icon={Sparkles}
        title="No pending AI suggestions"
        description="The agent's proposals will appear here for review before anything is committed to the document."
        variant="bare"
        data-testid="ai-suggestions-empty"
      />
    )
  }

  return (
    <div className="space-y-3" data-testid="ai-suggestions-list">
      <p className="text-xs text-muted-foreground">
        {actions.length} pending suggestion{actions.length === 1 ? '' : 's'}.
        Review each change before it's applied to the document — accepting
        runs the same audited write the chat agent would otherwise commit.
      </p>
      {actions.map((action) => (
        <AiSuggestionCard
          key={action.uuid}
          action={action}
          onAccept={() => void onAccept(action)}
          onReject={() => void onReject(action)}
          busy={busyUuid === action.uuid ? busyKind : null}
          anyBusy={busyUuid != null}
        />
      ))}
    </div>
  )
}

interface AiSuggestionCardProps {
  action: AiActionRead
  onAccept: () => void
  onReject: () => void
  busy: 'accept' | 'reject' | null
  anyBusy: boolean
}

function AiSuggestionCard({
  action,
  onAccept,
  onReject,
  busy,
  anyBusy,
}: AiSuggestionCardProps) {
  const title = action.title || defaultTitleFor(action.action_type)
  return (
    <Card
      className="space-y-3 border border-border/70 p-3"
      data-testid="ai-suggestion-card"
      data-action-uuid={action.uuid}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <Sparkles className="size-3 text-primary" aria-hidden />
            <span className="text-sm font-medium tracking-tight">{title}</span>
            <Badge tone="warning" size="xs">
              {humanizeActionType(action.action_type)}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Proposed by the agent — review before applying.
          </p>
        </div>
      </div>

      <ProposalSummary action={action} />

      <div className="flex items-center justify-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={onReject}
          disabled={anyBusy}
          data-testid="ai-suggestion-reject"
          aria-label={`Reject ${title}`}
        >
          {busy === 'reject' ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            <X aria-hidden />
          )}
          Reject
        </Button>
        <Button
          size="sm"
          onClick={onAccept}
          disabled={anyBusy}
          data-testid="ai-suggestion-accept"
          aria-label={`Accept ${title}`}
        >
          {busy === 'accept' ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            <Check aria-hidden />
          )}
          Accept
        </Button>
      </div>
    </Card>
  )
}

function ProposalSummary({ action }: { action: AiActionRead }) {
  const entries = extractFieldEdits(action)
  if (entries.length > 0) {
    return (
      <div className="space-y-2" data-testid="ai-suggestion-edits">
        {entries.map((entry, idx) => (
          <div
            key={`${entry.field_uuid}-${idx}`}
            className="rounded-md border border-border/70 bg-muted/40 px-3 py-2 text-xs"
          >
            <div className="font-medium text-foreground">
              {entry.friendly_name || entry.field_uuid}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-2 text-muted-foreground">
              <span className="line-through opacity-70">
                {renderValue(entry.before)}
              </span>
              <span aria-hidden>→</span>
              <span className="font-medium text-foreground">
                {renderValue(entry.after)}
              </span>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const after = readStringField(action.payload, 'after')
  const summary = readStringField(action.payload, 'summary')
  if (after || summary) {
    return (
      <div
        className="rounded-md border border-border/70 bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
        data-testid="ai-suggestion-context"
      >
        {summary ? <p className="font-medium text-foreground">{summary}</p> : null}
        {after ? (
          <p className="mt-1 whitespace-pre-wrap text-foreground/80">{after}</p>
        ) : null}
      </div>
    )
  }

  return (
    <p className="text-xs text-muted-foreground">
      No preview available for this suggestion.
    </p>
  )
}

function renderValue(value: string | null | undefined): string {
  if (value == null || value === '') return '∅'
  return value
}

function readStringField(
  payload: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  if (!payload) return null
  const raw = (payload as Record<string, unknown>)[key]
  return typeof raw === 'string' && raw.length > 0 ? raw : null
}

function extractFieldEdits(action: AiActionRead): AiFieldEditPayloadEntry[] {
  const payload = action.payload as Record<string, unknown> | null | undefined
  if (!payload) return []
  if (action.action_type === 'field_edit') {
    const edit = payload.edit
    if (isFieldEditPayload(edit)) return [edit]
  }
  if (action.action_type === 'batch_field_edit') {
    const edits = payload.edits
    if (Array.isArray(edits)) {
      return edits.filter(isFieldEditPayload)
    }
  }
  return []
}

function isFieldEditPayload(value: unknown): value is AiFieldEditPayloadEntry {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return typeof v.field_uuid === 'string'
}

function humanizeActionType(type: AiActionType): string {
  switch (type) {
    case 'field_edit':
      return 'Field edit'
    case 'batch_field_edit':
      return 'Batch edit'
    case 'project_setup':
      return 'Project setup'
    case 'context_change':
      return 'Context change'
  }
}

function defaultTitleFor(type: AiActionType): string {
  return `AI ${humanizeActionType(type).toLowerCase()}`
}
